import { useState, forwardRef, useEffect, useImperativeHandle } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../../utils/utils";
import StarIcon from "../../../assets/svgs/common/star.svg";

const textareaVariants = cva(
  "focus:ring-gap-1 px-6 py-6 border rounded-xl w-full font-figtree font-medium text-base tracking-[-0.32px] outline-none resize-none",
  {
    variants: {
      variant: {
        default: "border-black bg-white text-black focus:ring-2 ring-gray-300",
        error: "border-red-500 bg-white text-black",
        disabled: "border-gray-300 bg-gray-100 text-gray-500",
        "3d": "border-black bg-white text-black relative z-10", // 3D variant
      },
      size: {
        default: "px-6 py-6 text-base min-h-[120px]",
        sm: "p-2 text-base min-h-[80px]",
        lg: "p-4 text-xl min-h-[160px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size">,
    VariantProps<typeof textareaVariants> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  validateAs?: "required" | "none";
  onValidationChange?: (isValid: boolean, errorMessage?: string) => void;
  forceValidate?: boolean;
}

// Export a ref type that allows parent components to trigger validation
export interface TextareaRef {
  validate: () => boolean;
  getValue: () => string;
}

const Textarea = forwardRef<TextareaRef, TextareaProps>(
  (
    {
      label,
      variant,
      size,
      className,
      error: externalError,
      fullWidth = true,
      disabled,
      validateAs = "none",
      onValidationChange,
      forceValidate = false,
      ...props
    },
    ref
  ) => {
    const [value, setValue] = useState(props.defaultValue || "");
    const [internalError, setInternalError] = useState<string | undefined>(
      undefined
    );
    const [touched, setTouched] = useState(false);

    // Determine if there's an error (either external or internal)
    const error = externalError || internalError;

    // Validate the textarea based on validateAs prop
    const validateTextarea = (textareaValue: string): boolean => {
      if (
        validateAs === "required" &&
        (!textareaValue || textareaValue.trim() === "")
      ) {
        setInternalError("This field is required");
        onValidationChange?.(false, "This field is required");
        return false;
      }

      setInternalError(undefined);
      onValidationChange?.(true);
      return true;
    };

    // Expose methods to parent components via ref
    useImperativeHandle(ref, () => ({
      validate: () => {
        setTouched(true);
        return validateTextarea(
          props.value !== undefined
            ? (props.value as string)
            : (value as string)
        );
      },
      getValue: () =>
        props.value !== undefined ? (props.value as string) : (value as string),
    }));

    // Validate on value change if already touched
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
        useEffect(() => {
      if (touched && validateAs !== "none") {
        validateTextarea(
          props.value !== undefined
            ? (props.value as string)
            : (value as string)
        );
      }
    }, [value, props.value, touched]);

    // Force validation when forceValidate prop changes to true
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
        useEffect(() => {
      if (forceValidate && validateAs !== "none") {
        setTouched(true);
        validateTextarea(
          props.value !== undefined
            ? (props.value as string)
            : (value as string)
        );
      }
    }, [forceValidate]);

    return (
      <div className={cn("flex flex-col gap-2", fullWidth ? "w-full" : "")}>
        {label && (
          <label className="flex items-start font-cabinetgrotesk md:font-medium text-sm md:text-lg leading-none tracking-[-0.36px]">
            {label} {props.required && <StarIcon/>}
          </label>
        )}
        <div className="relative">
          {variant === "3d" && !disabled && !error && (
            <div className="top-0.5 left-0.5 absolute bg-black rounded-xl w-full h-full" />
          )}

          <textarea
            value={props.value !== undefined ? props.value : value}
            onChange={(e) => {
              const newValue = e.target.value;
              setValue(newValue);
              props.onChange && props.onChange(e);
            }}
            onBlur={(e) => {
              setTouched(true);
              if (validateAs !== "none") {
                validateTextarea(
                  props.value !== undefined
                    ? (props.value as string)
                    : (value as string)
                );
              }
              props.onBlur && props.onBlur(e);
            }}
            disabled={disabled}
            className={cn(
              textareaVariants({
                variant: disabled ? "disabled" : error ? "error" : variant,
                size,
              }),
              className
            )}
            {...props}
          />
        </div>
        {error && (
          <span className="font-cabinetgrotesk text-red-500 text-xs">
            {error}
          </span>
        )}
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export default Textarea;